import type { JobResponse } from '@siteday/shared';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { Link, Outlet, useOutletContext, useParams } from 'react-router';

import { ErrorState, LoadingState, PageHeader } from '@/components/PageHeader';
import { JobStatusPill } from '@/components/StatusPill';
import { Pill } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { useCompanyBasics } from '@/features/auth/session';
import { EmailReportDialog } from '@/features/entries/EmailReportDialog';
import { useEntriesList } from '@/features/entries/useEntries';
import { todayInTz, weekStartFor } from '@/lib/dates';

import { ReconcileDialog } from './ReconcileDialog';
import { useQboCandidates, useQboStatus } from './JobForm';
import { useJob } from './useJobs';

export interface JobOutletContext {
  job: JobResponse;
}

export function useJobContext(): JobOutletContext {
  return useOutletContext<JobOutletContext>();
}

/** W3 header + tabs (To-date · Entries · Cost codes · Settings); tab bodies are nested routes. */
export function JobDetailPage() {
  const { id = '' } = useParams();
  const job = useJob(id);
  const { tz } = useCompanyBasics();
  const today = todayInTz(tz);
  const qbo = useQboStatus();
  const candidates = useQboCandidates(qbo.data?.connected === true);
  const todayEntries = useEntriesList({ jobId: id, from: today, to: today });
  const [emailOpen, setEmailOpen] = useState(false);
  const [reconcile, setReconcile] = useState(false);

  if (job.isPending) return <LoadingState />;
  if (job.isError) return <ErrorState error={job.error} onRetry={() => void job.refetch()} />;
  const j = job.data;
  const customer = j.qboCustomerId
    ? (candidates.data?.customers.find((c) => c.id === j.qboCustomerId)?.name ?? j.qboCustomerId)
    : null;
  const todayEntry = todayEntries.data?.items[0];

  return (
    <>
      <PageHeader
        title={j.name}
        sub={
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            {j.clientName ? <span>{j.clientName}</span> : null}
            {j.siteAddress ? <span>· {j.siteAddress}</span> : null}
            <span>·</span>
            <JobStatusPill status={j.status} />
            {j.createdByForeman ? <Pill tone="orange">Created in the field</Pill> : null}
            {j.foremen.length ? (
              <span>· Foreman {j.foremen.map((f) => f.name).join(', ')}</span>
            ) : null}
            <span>· QuickBooks: {customer ?? <span className="text-ink3">not mapped</span>}</span>
          </span>
        }
        actions={
          <>
            {j.createdByForeman ? (
              <Button variant="dark" onClick={() => setReconcile(true)}>
                Reconcile
              </Button>
            ) : null}
            <Button onClick={() => setEmailOpen(true)} disabled={!todayEntry}>
              <Mail /> Email today's report
            </Button>
            <Link to={`/rollup?job=${j.id}&week=${weekStartFor(today, 'mon')}`}>
              <Button variant="primary">Weekly roll-up</Button>
            </Link>
          </>
        }
      />
      <Tabs
        items={[
          { key: 'to-date', label: 'To-date', to: `/jobs/${j.id}`, end: true },
          { key: 'entries', label: 'Entries', to: `/jobs/${j.id}/entries` },
          { key: 'cost-codes', label: 'Cost codes', to: `/jobs/${j.id}/cost-codes` },
          { key: 'settings', label: 'Settings', to: `/jobs/${j.id}/settings` },
        ]}
      />
      <Outlet context={{ job: j } satisfies JobOutletContext} />
      {todayEntry ? (
        <EmailReportDialog
          open={emailOpen}
          entryId={todayEntry.id}
          jobId={j.id}
          label={`${j.name} · today`}
          onClose={() => setEmailOpen(false)}
        />
      ) : null}
      <ReconcileDialog job={j} open={reconcile} onClose={() => setReconcile(false)} />
    </>
  );
}
